import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Cta, Hero, Para, Question, Section, Steps, linkStyle } from './Landing';

const PlayVsComputer = () => (
    <Box>
        <Helmet>
            <title>Play Sequence Against the Computer – Free, Easy to Hard | Play Sequence</title>
            <meta name="description" content="Play Sequence online against the computer for free. Pick Easy, Medium or Hard, choose your colour and board, and start in seconds. No sign-up, no download." />
            <meta property="og:title" content="Play Sequence Against the Computer – Free, Easy to Hard" />
            <meta property="og:description" content="A free Sequence opponent in your browser: Easy plays at random, Medium blocks and builds, Hard hunts for double threats." />
        </Helmet>

        <Hero
            title="Play Sequence Against the Computer"
            subtitle="A free opponent whenever you want a game. Easy, Medium or Hard, no sign-up."
        />

        <Container maxWidth="md" sx={{ py: 6 }}>
            <Section title="Start a game in three clicks">
                <Steps ordered items={[
                    <>Open <Link to="/" style={linkStyle}>play-sequence.com</Link>. The start screen appears on its own.</>,
                    <>Choose <strong>Play the computer</strong>, then pick a difficulty, your chip colour and the board.</>,
                    <>Press start. The cards are dealt straight away, and the computer takes its turns as soon as yours is done.</>,
                ]} />
                <Para last>
                    There's nothing to install and no account to make. Your games are remembered in this browser, so you can come back to one you left, see it in My games and watch it again as a replay. Sign in with Google if you want your results to follow you to another device.
                </Para>
            </Section>

            <Section title="Easy, Medium and Hard">
                <Steps items={[
                    <><strong>Easy</strong> plays a legal card at random. Good for learning the rules and the two kinds of Jacks.</>,
                    <><strong>Medium</strong> blocks your lines and builds its own. It punishes a sequence left open on one side.</>,
                    <><strong>Hard</strong> looks for double threats, two lines that each need one more chip, so you can only stop one. You'll need to block early and keep your two-eyed Jacks for the right moment.</>,
                ]} />
                <Para last>
                    Not sure how a card works? Turn <strong>highlights</strong> on and the board shows where your cards can go. Turn them off once you know the board by heart. For tactics that work against every level, read the <Link to="/strategy" style={linkStyle}>Sequence strategy guide</Link>.
                </Para>
            </Section>

            <Section title="Climb the leaderboard">
                <Para last>
                    Every win against the computer counts on the <Link to="/leaderboard" style={linkStyle}>leaderboard</Link>, ranked by the fewest moves and then the fastest time, with separate tables for Easy, Medium and Hard and for today, this week, this month and all time. As a guest you appear under a name made up for your browser; sign in with Google to choose your own.
                </Para>
            </Section>

            <Section title="Questions">
                <Question q="Is playing Sequence against the computer free?">
                    Yes. Every game is free, with no account, no download and no limit on how many you play.
                </Question>
                <Question q="Can I play on my phone?">
                    The board needs a wider screen to show all 100 spaces and your hand, so play on a computer, a laptop or a tablet turned sideways.
                </Question>
                <Question q="What rules does the computer play by?">
                    The standard Sequence rules: the corners count for everyone, two-eyed Jacks are wild and one-eyed Jacks remove a chip that isn't part of a finished sequence. See <Link to="/how-to-play" style={linkStyle}>how to play Sequence</Link> for the full rules.
                </Question>
                <Question q="Can I play with friends instead?">
                    Yes. Invite them with a link and play in teams, with video calls built in. See <Link to="/play-with-friends" style={linkStyle}>playing Sequence with friends</Link>.
                </Question>
            </Section>

            <Cta title="Ready for a game?" text="The computer is always ready for a game." />
        </Container>
    </Box>
);

export default PlayVsComputer;
